import { GraphQLResponse } from '../types';
import { TaxDeductionBehaviour as TaxDeductionBehaviourType } from 'components/SalaryComponents/types';

export const PaymentSource = {
  WITHIN_PAYROLL: 'WITHIN_PAYROLL',
  ALREADY_PAID: 'ALREADY_PAID',
} as const;
export type PaymentSourceType = typeof PaymentSource[keyof typeof PaymentSource];

export const AssignmentStatus = {
  ACTIVE: 'ASSIGNMENT_STATUS_ACTIVE',
  CANCELLED: 'ASSIGNMENT_STATUS_CANCELLED',
} as const;
export type AssignmentStatusType = typeof AssignmentStatus[keyof typeof AssignmentStatus];

export const PaymentStatus = {
  PENDING: 'PAYMENT_STATUS_PENDING',
  PAID: 'PAYMENT_STATUS_PAID',
  CLAWBACK_APPLICABLE: 'PAYMENT_STATUS_CLAWBACK_APPLICABLE',
  CANCELLED: 'PAYMENT_STATUS_CANCELLED',
} as const;
export type PaymentStatusType = typeof PaymentStatus[keyof typeof PaymentStatus];

export type BonusDisbursement = {
  id: string;
  assignment_id: string;
  tenant_id: string | null;
  merchant_id: string | null;
  user_id: string;
  payroll_month: string;
  amount: string;
  payment_status: PaymentStatusType | null;
  clawback_fnf_payroll_month: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type BonusAssignment = {
  id: string;
  tenant_id: string | null;
  merchant_id: string | null;
  bonus_component_id: string;
  user_id: string;
  payment_source: PaymentSourceType | null;
  clawback_applicable: boolean | null;
  clawback_period: number | null;
  assignment_status: AssignmentStatusType | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
  disbursements: BonusDisbursement[] | null;
};

export type CreateBonusAssignmentInput = {
  bonus_component_id: string;
  user_id: string;
  amount: string;
  payroll_month: string;
  payment_source?: PaymentSourceType;
  clawback_applicable?: boolean;
  clawback_period?: number;
  remarks?: string;
};

export type ListBonusAssignmentsInput = {
  user_id?: string;
  component_id?: string;
  payment_status?: PaymentStatusType;
  payroll_month?: string;
  limit?: number;
  offset?: number;
};

export type UpdateBonusAssignmentInput = {
  id: string;
  amount?: string;
  remarks?: string;
  clawback_applicable?: boolean;
  clawback_period?: number;
  payroll_month?: string;
  payment_status?: Extract<PaymentStatusType, 'PAYMENT_STATUS_PENDING' | 'PAYMENT_STATUS_PAID'>;
};

export type CancelBonusAssignmentInput = {
  id: string;
};

export const TaxDeductionBehaviour = {
  INSTANT: 'instant',
  PRO_RATED: 'pro_rated',
  NOT_APPLICABLE: 'not_applicable',
} as const;

export type BonusComponentListItem = {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean | null;
  settings: {
    flexible_settings: {
      tax_behaviour: {
        tax_deduction_behaviour: TaxDeductionBehaviourType | null;
      } | null;
    } | null;
  } | null;
};

export const ComponentModule = {
  FBP: 'fbp',
  BONUS: 'bonus',
} as const;
export type ComponentModuleType = typeof ComponentModule[keyof typeof ComponentModule];

export type ListBonusComponentsInput = {
  category: 'BONUS_CATEGORY';
  type?: ComponentModuleType;
  status?: 'SALARY_COMPONENT_STATUS_ACTIVE' | 'SALARY_COMPONENT_STATUS_INACTIVE';
  limit?: number;
  offset?: number;
};

export type ListBonusComponentsVariables = { input: ListBonusComponentsInput };

export type ListBonusComponentsResponse = {
  payrollListFlexibleBenefitsComponents: {
    code: number;
    success: boolean;
    message: string;
    total_count: number | null;
    data: BonusComponentListItem[] | null;
  };
};

export type CreateBonusAssignmentVariables = { input: CreateBonusAssignmentInput };
export type ListBonusAssignmentsVariables = { input: ListBonusAssignmentsInput };
export type UpdateBonusAssignmentVariables = { input: UpdateBonusAssignmentInput };
export type CancelBonusAssignmentVariables = { input: CancelBonusAssignmentInput };

export type CreateBonusAssignmentResponse = GraphQLResponse<
  'payrollCreateBonusAssignment',
  Pick<BonusAssignment, 'id'> | null
>;
export type UpdateBonusAssignmentResponse = GraphQLResponse<
  'payrollUpdateBonusAssignment',
  Pick<BonusAssignment, 'id'> | null
>;

export type ListBonusAssignmentsResponse = {
  payrollListBonusAssignments: {
    code: number;
    success: boolean;
    message: string;
    data: BonusAssignment[] | null;
    total_count: number | null;
    limit: number | null;
    offset: number | null;
  };
};

export type CancelBonusAssignmentResponse = {
  payrollCancelBonusAssignment: {
    code: number;
    success: boolean;
    message: string;
  };
};
